// The root (A5P13): signed in, it goes to the last organization or to /welcome; signed out, it shows the Welcome.
// The probe is a plain request, not a cached query, so a signed-out visitor is not sent to sign-in by the 401 handler.
import { ids } from '@orca/api-client/testIds'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '../components/ui/button'
import { GetInWindow, Mark } from '../features/auth/AuthFrame'
import '../features/auth/strings'
import '../features/demo/strings'
import { api } from '../shared/api/api'
import { useFlags } from '../shared/appConfig/flagOverrides'
import { landingFor } from '../shared/org/lastOrg'
import { keys } from '../shared/query/keys'

export const Route = createFileRoute('/')({
  staticData: { screenId: 'AuthWelcomeScreen', access: 'signed out' },
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await api
      .GET('/me')
      .then((answer) => answer.data ?? null)
      .catch(() => null)
    if (!me) return
    queryClient.setQueryData(keys.me(), me)
    const landing = landingFor(me.memberships ?? [])
    if (landing) throw redirect({ to: '/o/$orgSlug', params: { orgSlug: landing.slug } })
    throw redirect({ to: '/welcome' })
  },
  component: AuthWelcomeScreen,
})

// The Welcome (board getin-welcome): the mark on the glacier, then the ways in — Create account (while sign-ups are
// open), Sign in, and Try the demo (while the demo is on). Below 600 px the glacier is the screen's head with its jaw
// and the buttons sit on the page; from 600 px the same choices are one card on the glacier.
function AuthWelcomeScreen() {
  const { t } = useTranslation()
  const flags = useFlags()
  return (
    <GetInWindow testId={ids.authWelcome.root}>
      <div className="getin-head flex flex-1 flex-col items-center justify-center gap-(--stack-sm) px-(--inset-lg) pt-(--inset-xl) pb-[72px] text-center medium:flex-none medium:pb-0">
        <span data-testid={ids.authWelcome.logoValue}>
          <Mark size={72} />
        </span>
        <h1 className="font-bold font-display text-[40px] leading-[44px] tracking-[-0.05em]">orca</h1>
        <p className="getin-sub font-semibold text-callout">{t('auth.welcome.tagline')}</p>
      </div>
      <div className="flex flex-col gap-(--stack-sm) p-(--inset-lg) pb-(--inset-xl)">
        {flags?.signups !== false && (
          <Button asChild block size="lg">
            <Link to="/sign-up" data-testid={ids.authWelcome.signUpButton}>
              {t('auth.welcome.signUp')}
            </Link>
          </Button>
        )}
        <Button asChild block size="lg" variant="secondary">
          <Link to="/sign-in" data-testid={ids.authWelcome.signInButton}>
            {t('auth.welcome.signIn')}
          </Link>
        </Button>
        {flags?.demo && (
          <Button asChild block variant="ghost">
            <Link to="/demo" data-testid={ids.authWelcome.demoButton}>
              {t('demo.welcome.button')}
            </Link>
          </Button>
        )}
      </div>
    </GetInWindow>
  )
}
